/**
 * ProviderAppConfigRow — 商户账户组内的一个 App 配置行（设计稿 PayerMax A1 / A3 / A6）。
 *
 * 展示：App 与平台、连接状态（待验证 / 已连接 / 已失效）、启停、需重新测试 / 未测试、版本与验证口径、原生回跳、
 * 五步进度；已失效时按 `last_test_result.reason` 说明原因。操作：测试连接、启用（未连接时预先禁用并说明）/
 * 停用（需确认）、编辑。只读角色全部写控件禁用并写明原因；测试进行中本行其他写按钮一并禁用。
 */

import type {
  PaymentProviderConfigResponse,
  PaymentProviderWebhookEndpointSummary,
} from '@novel-admin/shared';
import type { AppRow } from '~/data/queries';
import { fmtDateTime } from '~/lib/format';
import { Icons } from '~/lib/icons';
import { AppIcon } from '~/ui/AppIcon';
import { ProviderNoticeBlock } from './ProviderNoticeBlock';
import { ProviderSetupProgress } from './ProviderSetupProgress';
import {
  CONNECTION_STATUS_META,
  chipClass,
  configSummaryLine,
  isConfigSideReady,
  lastTestFreshness,
  providerPlatformOf,
  setupCurrentStep,
} from './payment-provider-config-view';
import type { ProviderNotice } from './payment-provider-error-notice';

const REASON_TEXT: Record<string, { title: string; body: string }> = {
  CREDENTIAL_INVALID: {
    title: 'PayerMax 拒绝了这组凭据',
    body: '商户号、应用 ID、私钥、平台公钥或 key 版本有误，或填的是另一环境的凭据。',
  },
  PERMISSION_DENIED: {
    title: 'PayerMax 拒绝了访问权限',
    body: '商户账户未授予所需权限。请在 PayerMax 后台核对后重新测试。',
  },
};

export type RowPendingAction = 'test' | 'activate' | 'deactivate' | null;

export function ProviderAppConfigRow({
  config,
  app,
  endpoints,
  writeBlockedReason,
  editBlockedReason,
  pending,
  notice,
  onTest,
  onActivate,
  onDeactivate,
  onEdit,
  onReplaceCredential,
  onDismissNotice,
  onGoToEndpoints,
}: {
  config: PaymentProviderConfigResponse;
  app: AppRow | undefined;
  endpoints: PaymentProviderWebhookEndpointSummary[];
  /** 非空 = 写控件禁用的原因（只读角色） */
  writeBlockedReason: string | null;
  /** 非空 = 编辑禁用的原因（只读角色或能力声明未读到） */
  editBlockedReason: string | null;
  pending: RowPendingAction;
  notice: ProviderNotice | null;
  onTest: () => void;
  onActivate: () => void;
  onDeactivate: () => void;
  onEdit: () => void;
  onReplaceCredential: () => void;
  onDismissNotice: () => void;
  onGoToEndpoints: () => void;
}) {
  const platform = providerPlatformOf(app);
  const name = app?.name ?? config.app_id;
  const status = CONNECTION_STATUS_META[config.connection_status];
  const freshness = lastTestFreshness(config);
  const current = setupCurrentStep(config, endpoints);
  const ready = isConfigSideReady(config, platform);
  const busy = pending !== null;
  const writeDisabled = writeBlockedReason !== null || busy;
  const last = config.last_test_result;

  const activateBlocked =
    config.connection_status === 'connected'
      ? null
      : config.connection_status === 'invalid'
        ? '先测试连接：当前为已失效'
        : freshness === 'stale'
          ? '先测试连接：配置保存后回到待验证'
          : '先测试连接';

  return (
    <div style={{ padding: '10px 16px 12px', borderTop: '1px solid var(--border)' }}>
      <div className="hstack" style={{ gap: 12, flexWrap: 'wrap' }}>
        <AppIcon app={app} label={app ? undefined : '?'} size="sm" />
        <div style={{ flex: 1, minWidth: 280 }}>
          <div className="hstack" style={{ gap: 6, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 500, fontSize: 13 }}>{name}</span>
            <span className="chip mono" style={{ fontSize: 9.5 }}>
              {platform ?? '平台未知'}
            </span>
            <span className={chipClass(status.tone)} style={{ fontSize: 9.5 }}>
              {status.label}
            </span>
            <span className={config.is_active ? 'chip chip-pos' : 'chip'} style={{ fontSize: 9.5 }}>
              {config.is_active ? '已启用' : '未启用'}
            </span>
            {freshness === 'stale' && (
              <span className="chip chip-warn" style={{ fontSize: 9.5 }}>
                <Icons.IconRefresh size={9} />
                需重新测试
              </span>
            )}
            {freshness === 'never' && config.connection_status === 'unverified' && (
              <span className="chip" style={{ fontSize: 9.5 }}>
                未测试
              </span>
            )}
          </div>
          <div
            className="mono"
            style={{
              fontSize: 10.5,
              color: 'var(--fg-faint)',
              marginTop: 2,
              wordBreak: 'break-all',
            }}
          >
            {configSummaryLine(config, platform)}
          </div>
        </div>
        <ProviderSetupProgress current={current} doneLabel="配置侧已就绪" />
        <div className="hstack" style={{ gap: 6 }}>
          <button
            type="button"
            className={`btn btn-sm${current === 4 && !writeBlockedReason ? ' btn-accent' : ''}`}
            disabled={writeDisabled}
            title={writeBlockedReason ?? undefined}
            onClick={onTest}
          >
            {pending === 'test' ? (
              <>
                <span className="spinner" style={{ width: 10, height: 10 }} />
                测试中…
              </>
            ) : (
              '测试连接'
            )}
          </button>
          {config.is_active ? (
            <button
              type="button"
              className={`btn btn-sm${writeBlockedReason ? '' : ' btn-danger'}`}
              disabled={writeDisabled}
              title={writeBlockedReason ?? undefined}
              onClick={onDeactivate}
            >
              {pending === 'deactivate' ? '停用中…' : '停用'}
            </button>
          ) : (
            <button
              type="button"
              className={`btn btn-sm${current === 5 && !writeBlockedReason ? ' btn-accent' : ''}`}
              disabled={writeDisabled || activateBlocked !== null}
              title={writeBlockedReason ?? activateBlocked ?? undefined}
              onClick={onActivate}
            >
              {pending === 'activate' ? '启用中…' : '启用'}
            </button>
          )}
          <button
            type="button"
            className="btn btn-ghost btn-icon"
            aria-label={`编辑 ${name} 配置`}
            disabled={editBlockedReason !== null || busy}
            title={editBlockedReason ?? `编辑 ${name} 配置`}
            onClick={onEdit}
            style={{ width: 26, height: 26 }}
          >
            <Icons.IconEdit size={12} />
          </button>
        </div>
      </div>

      {ready && (
        <div style={{ fontSize: 11, color: 'var(--fg-faint)', margin: '6px 0 0 40px' }}>
          配置侧条件已满足（已启用 + 已连接{platform === 'WEB' ? '' : ' + 回跳已填'}
          ）。商户合约、支付方式开通与商品价格不在本页判定，见 App 详情「支付渠道」阻断项。
        </div>
      )}

      {config.connection_status === 'invalid' && (
        <div
          className="hstack"
          style={{
            gap: 8,
            alignItems: 'flex-start',
            margin: '8px 0 0 40px',
            padding: '8px 10px',
            background: 'var(--neg-soft)',
            borderRadius: 'var(--radius)',
          }}
        >
          <Icons.IconAlert size={13} style={{ color: 'var(--neg)', marginTop: 2, flexShrink: 0 }} />
          <div style={{ flex: 1, fontSize: 11.5, lineHeight: 1.55 }}>
            {last?.status === 'invalid' &&
            last.reason &&
            last.config_version === config.config_version ? (
              <>
                <span style={{ fontWeight: 500 }}>
                  {REASON_TEXT[last.reason]?.title ?? last.reason}
                </span>
                <span className="mono" style={{ color: 'var(--fg-muted)' }}>
                  {' '}
                  · {last.reason} · {fmtDateTime(last.tested_at)} · v{last.config_version}
                </span>
                <br />
                <span style={{ color: 'var(--fg-muted)' }}>{REASON_TEXT[last.reason]?.body}</span>
              </>
            ) : (
              <>
                <span style={{ fontWeight: 500 }}>连接已失效</span>
                <br />
                <span style={{ color: 'var(--fg-muted)' }}>
                  在连接测试之外被判定失效，或尚未用当前版本测试。重新测试，或更换凭据后再测试。
                </span>
              </>
            )}
          </div>
          <button
            type="button"
            className="btn btn-sm"
            disabled={editBlockedReason !== null || busy}
            title={editBlockedReason ?? undefined}
            onClick={onReplaceCredential}
          >
            更换凭据
          </button>
        </div>
      )}

      {notice && (
        <ProviderNoticeBlock
          notice={notice}
          style={{ margin: '8px 0 0 40px' }}
          onDismiss={onDismissNotice}
          actions={
            notice.kind === 'material-missing' ? (
              <button type="button" className="btn btn-sm" onClick={onGoToEndpoints}>
                {endpoints.length === 0 ? '去登记回调' : '去写入材料'}
              </button>
            ) : notice.kind === 'config-incomplete' ? (
              <button
                type="button"
                className="btn btn-sm"
                disabled={editBlockedReason !== null}
                onClick={onEdit}
              >
                编辑配置
              </button>
            ) : notice.kind === 'route-missing' ? (
              <button
                type="button"
                className="btn btn-sm"
                disabled={editBlockedReason !== null}
                onClick={onReplaceCredential}
              >
                更换凭据
              </button>
            ) : null
          }
        />
      )}
    </div>
  );
}
